import { ParkingtonTool, type ParkingtonState } from '../ParkingtonTypes';
import type { InputCallbacks } from './InputTypes';

/**
 * Handles touch gestures (pinch zoom, pan, wheel zoom) and snap-back animation.
 */
export class TouchZoomHandler {
  // Constants
  private readonly MIN_SCALE = 0.5;
  private readonly WHEEL_ZOOM_FACTOR = 0.1;
  private readonly PINCH_ZOOM_SENSITIVITY = 1.5;
  private readonly TWO_FINGER_COOLDOWN = 200;
  private readonly TRANSFORM_DEBOUNCE = 150;
  private readonly SNAP_BACK_STEPS = 10;

  // Touch state
  private lastTouchDistance: number | null = null;
  private lastTouchCenter: { x: number; y: number } | null = null;
  private touchStartPositions: Map<number, { x: number; y: number }> = new Map();
  private wasTwoFingerGesture = false;
  private twoFingerGestureEndTime = 0;

  // Rendering optimization during gestures
  private _isActivelyTransforming = false;
  private transformEndTimer: ReturnType<typeof setTimeout> | null = null;

  // Snap-back animation
  private snapBackStepsRemaining = 0;
  private snapBackTargetOffset: { x: number; y: number } | null = null;
  private snapBackStartOffset: { x: number; y: number } | null = null;

  get isTransforming(): boolean {
    return this._isActivelyTransforming;
  }

  /**
   * Mark viewport as actively transforming (for rendering optimization).
   */
  markTransforming(terrain: { setTransforming(v: boolean): void }): void {
    this._isActivelyTransforming = true;
    terrain.setTransforming(true);
    if (this.transformEndTimer) {
      clearTimeout(this.transformEndTimer);
    }
    this.transformEndTimer = setTimeout(() => {
      this._isActivelyTransforming = false;
      terrain.setTransforming(false);
      this.transformEndTimer = null;
    }, this.TRANSFORM_DEBOUNCE);
  }

  handleWheel(e: WheelEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    e.preventDefault();

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const currentScale = state.camera.getScale();
    const isZoomingOut = e.deltaY > 0;

    if (isZoomingOut && currentScale <= this.MIN_SCALE) {
      callbacks.triggerSnapBack();
      return;
    }

    const zoomFactor = e.deltaY < 0
      ? 1 + this.WHEEL_ZOOM_FACTOR
      : 1 / (1 + this.WHEEL_ZOOM_FACTOR);

    state.camera.zoomAtPoint(mouseX, mouseY, zoomFactor);
    callbacks.markTransforming();
  }

  handleTouchStart(e: TouchEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    for (const touch of Array.from(e.touches)) {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      this.touchStartPositions.set(touch.identifier, {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    }

    if (e.touches.length === 2) {
      e.preventDefault();
      this.wasTwoFingerGesture = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      this.lastTouchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const rect = (e.target as HTMLElement).getBoundingClientRect();
      this.lastTouchCenter = {
        x: (touch1.clientX + touch2.clientX) / 2 - rect.left,
        y: (touch1.clientY + touch2.clientY) / 2 - rect.top,
      };
    } else if (e.touches.length === 1) {
      if (this.wasTwoFingerGesture && (performance.now() - this.twoFingerGestureEndTime) < this.TWO_FINGER_COOLDOWN) {
        return;
      }
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      const touchY = e.touches[0].clientY - rect.top;
      callbacks.setMousePosition(touchX, touchY);
      callbacks.setHoveredCell(callbacks.screenToCell(touchX, touchY));
    }
  }

  handleTouchMove(e: TouchEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    if (e.touches.length === 2 && this.lastTouchDistance !== null && this.lastTouchCenter !== null) {
      e.preventDefault();
      callbacks.markTransforming();

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      const newDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const newCenter = {
        x: (touch1.clientX + touch2.clientX) / 2 - rect.left,
        y: (touch1.clientY + touch2.clientY) / 2 - rect.top,
      };

      // Pan
      const panDeltaX = newCenter.x - this.lastTouchCenter.x;
      const panDeltaY = newCenter.y - this.lastTouchCenter.y;
      const currentOffset = state.camera.getOffset();
      state.camera.setOffset(currentOffset.x + panDeltaX, currentOffset.y + panDeltaY);

      // Zoom
      const currentScale = state.camera.getScale();
      const rawZoomFactor = newDistance / this.lastTouchDistance;
      const zoomFactor = 1 + (rawZoomFactor - 1) * this.PINCH_ZOOM_SENSITIVITY;
      const isPinchingIn = zoomFactor < 1;

      if (isPinchingIn && currentScale <= this.MIN_SCALE) {
        callbacks.triggerSnapBack();
      } else {
        state.camera.zoomAtPoint(newCenter.x, newCenter.y, zoomFactor);
      }

      this.lastTouchDistance = newDistance;
      this.lastTouchCenter = newCenter;
    } else if (e.touches.length === 1) {
      if (this.wasTwoFingerGesture && (performance.now() - this.twoFingerGestureEndTime) < this.TWO_FINGER_COOLDOWN) {
        return;
      }

      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      const touchY = e.touches[0].clientY - rect.top;

      // Pan mode
      if (state.isPanMode) {
        e.preventDefault();
        callbacks.markTransforming();
        if (state.panTouchStart) {
          const dx = touchX - state.panTouchStart.x;
          const dy = touchY - state.panTouchStart.y;
          const currentOffset = state.camera.getOffset();
          state.camera.setOffset(currentOffset.x + dx, currentOffset.y + dy);
        }
        callbacks.setPanTouchStart({ x: touchX, y: touchY });
        callbacks.setMousePosition(touchX, touchY);
        return;
      }

      // Paint mode
      callbacks.setMousePosition(touchX, touchY);
      const hoveredCell = callbacks.screenToCell(touchX, touchY);
      callbacks.setHoveredCell(hoveredCell);

      if (state.isMouseDown && hoveredCell) {
        callbacks.applyBrush(hoveredCell.x, hoveredCell.y);
      }
    }
  }

  handleTouchEnd(e: TouchEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    for (const touch of Array.from(e.changedTouches)) {
      this.touchStartPositions.delete(touch.identifier);
    }

    if (e.touches.length < 2 && this.lastTouchDistance !== null) {
      this.twoFingerGestureEndTime = performance.now();
    }

    if (e.touches.length < 2) {
      this.lastTouchDistance = null;
      this.lastTouchCenter = null;
    }

    if (e.touches.length === 0) {
      callbacks.setIsMouseDown(false);
      callbacks.setPanTouchStart(null);
      if (this.wasTwoFingerGesture) {
        setTimeout(() => { this.wasTwoFingerGesture = false; }, this.TWO_FINGER_COOLDOWN);
      }
    }
  }

  /**
   * Trigger snap-back animation to center the view.
   *
   * The centred offset used to be recomputed here from a copy of
   * `centerInViewport`'s arithmetic, with the cell height and a 40 × 30 map
   * written in as literals and a comment admitting it — "we need cols+rows but
   * don't have direct access". The camera does that sum now, and the grid it
   * asks is the real one.
   */
  triggerSnapBack(state: ParkingtonState): void {
    if (this.snapBackStepsRemaining > 0) return;

    const centeredOffset = state.camera.centeredOffset(
      state.width,
      state.height,
      state.grid.getCols(),
      state.grid.getRows(),
    );
    const currentOffset = state.camera.getOffset();

    const dx = Math.abs(centeredOffset.x - currentOffset.x);
    const dy = Math.abs(centeredOffset.y - currentOffset.y);
    if (dx < 5 && dy < 5) return;

    this.snapBackStartOffset = { ...currentOffset };
    this.snapBackTargetOffset = centeredOffset;
    this.snapBackStepsRemaining = this.SNAP_BACK_STEPS;
  }

  /**
   * Update snap-back animation (called each frame).
   */
  updateSnapBack(state: ParkingtonState): void {
    if (this.snapBackStepsRemaining <= 0 || !this.snapBackTargetOffset || !this.snapBackStartOffset) {
      return;
    }

    this.snapBackStepsRemaining--;

    const t = 1 - (this.snapBackStepsRemaining / this.SNAP_BACK_STEPS);
    const eased = t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const startOffset = this.snapBackStartOffset;
    const targetOffset = this.snapBackTargetOffset;

    const newX = startOffset.x + (targetOffset.x - startOffset.x) * eased;
    const newY = startOffset.y + (targetOffset.y - startOffset.y) * eased;

    state.camera.setOffset(newX, newY);

    if (this.snapBackStepsRemaining === 0) {
      state.camera.setOffset(targetOffset.x, targetOffset.y);
      this.snapBackTargetOffset = null;
      this.snapBackStartOffset = null;
    }
  }
}
